import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BirthdayVoucher } from '@browsing/birthday-voucher';
import { getCustomer } from '@client/redux/modules/customer';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import { showHeaderFlashMessage } from '@client/redux/modules/flashMessage/actions';
import { ErrorBoundary } from '@browsing/components';
import { useLazyVariant } from '@client/lib/assignments';
import { useLoyalty } from '../hooks/use-loyalty';
import { useBirthdayVoucherVisibility } from './useBirthdayVoucherVisibility';

/**
 * Connected wrapper that injects customer data from Redux into BirthdayVoucher package
 * This is the only place where we connect the pure package to app-specific Redux state
 */
export const BirthdayVoucherConnected: React.FC = () => {
    const customer = useSelector(getCustomer);
    const loyalty = useLoyalty();
    const isMobileScreen = useSelector(getIsMobileScreen);
    const dispatch = useDispatch();

    const { isBirthdayVoucherShown } = useBirthdayVoucherVisibility();

    const [isExperimentEnabled, sendFeedback] = useLazyVariant(
        'birthday-voucher-experiment',
        'birthday-voucher-treatment',
    );

    const handleShowFlashMessage = (message: string) => {
        dispatch(
            showHeaderFlashMessage({
                type: 'success',
                message,
            }),
        );
    };

    useEffect(() => {
        if (!isBirthdayVoucherShown) {
            return;
        }
        sendFeedback();
    }, [isBirthdayVoucherShown, sendFeedback]);

    // Early return if feature disabled, another modal is shown, customer not loaded, or user not in experiment treatment
    if (!isBirthdayVoucherShown || !isExperimentEnabled) {
        return null;
    }

    const { birthday, firstName } = customer!;

    return (
        <ErrorBoundary componentName="BirthdayVoucherBanner">
            <BirthdayVoucher
                birthday={birthday}
                customerName={firstName}
                loyaltyTier={loyalty?.summary?.tier}
                loyaltyBenefits={loyalty?.summary?.benefits}
                isMobileScreen={isMobileScreen}
                onShowFlashMessage={handleShowFlashMessage}
            />
        </ErrorBoundary>
    );
};
